import type { LucideIcon } from "lucide-react";

/**
 * Color themes for tag groups.
 * Each color maps to a Tailwind color class.
 * Brand colors: project (transparent), section1 (black), section2 (tan/brown), section3 (gray)
 */
export type TagGroupColor =
  | "blue"
  | "purple"
  | "emerald"
  | "amber"
  | "project"
  | "section1"
  | "section2"
  | "section3";

/**
 * Represents a single tag within a group.
 * Filter ID format must match existing format for backward compatibility:
 * - Projects: `project-{project_id}`
 * - Categories: `category-{category_name}`
 * - Disciplines: `construction-{discipline_id}`
 * - Stages: `stage-{stage_id}`
 */
export interface Tag {
  /** Filter ID format: project-{id}, category-{name}, construction-{id}, stage-{id} */
  id: string;
  /** Display name */
  label: string;
  /** Number of documents with this tag (inclusive count for categories) */
  count: number;
  /** Whether this tag should render as indented subcategory (categories only) */
  isSubcategory?: boolean;
  /** Parent tag ID for grouping (categories only) */
  parentTagId?: string;
  /** Child tag IDs for inclusive filtering expansion (categories only) */
  childTagIds?: string[];
  /** Numeric category ID for filter lookups (categories only) */
  categoryId?: number;
  /** Multiple category IDs when tags are merged by name (for deduplicated filter items) */
  categoryIds?: number[];
}

/**
 * Represents a group of related tags.
 */
export interface TagGroup {
  /** Group identifier: projects, categories, disciplines, stages */
  id: string;
  /** Display label for the group */
  label: string;
  /** Lucide icon component for the group */
  icon: LucideIcon;
  /** Color theme for chips in this group */
  color: TagGroupColor;
  /** Available tags in this group */
  tags: Tag[];
}

/**
 * State for the tag filter component.
 */
export interface TagFilterState {
  /** Currently selected filter IDs */
  activeFilters: string[];
  /** Search input value for filtering dropdown options */
  searchQuery: string;
  /** Currently open group dropdown ID, or null if none open */
  openDropdown: string | null;
}

/**
 * Configuration for extracting tags from a specific group.
 * Used internally by useTagGroups hook.
 */
export interface TagGroupConfig {
  /** Group identifier */
  id: string;
  /** Display label */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Color theme */
  color: TagGroupColor;
}

/**
 * Color utilities for tag groups.
 * Provides Tailwind classes for chip backgrounds, text, and indicators.
 */
export const TAG_GROUP_COLORS: Record<
  TagGroupColor,
  {
    chip: string;
    chipText: string;
    indicator: string;
    cardBorder: string;
    cardBg: string;
  }
> = {
  blue: {
    chip: "bg-blue-100 hover:bg-blue-200",
    chipText: "text-blue-800",
    indicator: "bg-blue-500",
    cardBorder: "border-blue-300",
    cardBg: "bg-blue-50",
  },
  purple: {
    chip: "bg-purple-100 hover:bg-purple-200",
    chipText: "text-purple-800",
    indicator: "bg-purple-500",
    cardBorder: "border-purple-300",
    cardBg: "bg-purple-50",
  },
  emerald: {
    chip: "bg-emerald-100 hover:bg-emerald-200",
    chipText: "text-emerald-800",
    indicator: "bg-emerald-500",
    cardBorder: "border-emerald-300",
    cardBg: "bg-emerald-50",
  },
  amber: {
    chip: "bg-amber-100 hover:bg-amber-200",
    chipText: "text-amber-800",
    indicator: "bg-amber-500",
    cardBorder: "border-amber-300",
    cardBg: "bg-amber-50",
  },
  // Brand colors matching document tags
  project: {
    // Transparent background with dark text and black border (for project filter chips)
    chip: "bg-transparent hover:bg-neutral-100 border border-neutral-800",
    chipText: "text-neutral-800",
    indicator: "bg-neutral-600",
    cardBorder: "border-neutral-300",
    cardBg: "bg-neutral-50",
  },
  section1: {
    // S1: Black (#161616) - categories
    chip: "bg-[#161616] hover:bg-[#262626] border-0",
    chipText: "text-white",
    indicator: "bg-[#161616]",
    cardBorder: "border-neutral-400",
    cardBg: "bg-neutral-100",
  },
  section2: {
    // S2: Tan/Brown (#a5815d) - stages
    chip: "bg-[#a5815d] hover:bg-[#8a6b4d] border-0",
    chipText: "text-white",
    indicator: "bg-[#a5815d]",
    cardBorder: "border-[#a5815d]/50",
    cardBg: "bg-[#a5815d]/10",
  },
  section3: {
    // S3: Gray (#808080) - disciplines
    chip: "bg-[#808080] hover:bg-[#666666] border-0",
    chipText: "text-white",
    indicator: "bg-[#808080]",
    cardBorder: "border-neutral-400",
    cardBg: "bg-neutral-100",
  },
};
